import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Eye, EyeOff, LockKeyhole, ShieldCheck } from "lucide-react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { LOGIN } from "@/constants/testIds/auth";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = "Login · CashFUX";
  }, []);

  if (user) return <Navigate to="/dashboard" replace />;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
      navigate(location.state?.from || "/dashboard", { replace: true });
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#05060a] text-white">
      <div className="pointer-events-none absolute inset-0 grid-bg-fine opacity-50" />
      <div className="pointer-events-none absolute -left-24 top-1/3 h-80 w-80 rounded-full bg-[#e01e37]/15 blur-[120px]" />

      <div className="relative z-10 mx-auto grid min-h-screen max-w-[1440px] lg:grid-cols-[1.1fr_0.9fr]">
        <section className="hidden border-r border-white/10 px-12 py-10 lg:flex lg:flex-col xl:px-20 xl:py-14">
          <Link to="/" className="inline-flex w-fit items-center font-display text-2xl font-extrabold tracking-tight">
            Cash<span className="text-[#e01e37]">FUX</span>
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="my-auto max-w-xl"
          >
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#e01e37]">Control Center</p>
            <h1 className="mt-6 font-display text-5xl font-extrabold leading-[0.98] tracking-[-0.04em] xl:text-7xl">
              Finanzströme.<br />Ein Blick.<br /><span className="text-white/30">Volle Kontrolle.</span>
            </h1>
            <p className="mt-8 max-w-md text-base leading-7 text-white/50">
              Konten, Zahlungen und operative Aufgaben in einer geschützten Arbeitsoberfläche.
            </p>
          </motion.div>
          <div className="flex items-center gap-3 border-t border-white/10 pt-6 text-xs text-white/40">
            <ShieldCheck className="h-4 w-4 text-[#e01e37]" />
            Verschlüsselte Sitzung · Zugriff nur für autorisierte Nutzer
          </div>
        </section>

        <section className="flex min-h-screen items-center px-6 py-10 sm:px-12 lg:px-16 xl:px-24">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12, duration: 0.55 }}
            className="mx-auto w-full max-w-md"
          >
            <div className="mb-14 flex items-center justify-between lg:hidden">
              <Link to="/" className="font-display text-2xl font-extrabold">Cash<span className="text-[#e01e37]">FUX</span></Link>
              <Link to="/" className="text-white/45 hover:text-white" aria-label="Zur Startseite"><ArrowLeft className="h-5 w-5" /></Link>
            </div>

            <div className="flex h-11 w-11 items-center justify-center border border-[#e01e37]/40 bg-[#e01e37]/10 text-[#e01e37]">
              <LockKeyhole className="h-5 w-5" />
            </div>
            <h2 className="mt-8 font-display text-4xl font-bold tracking-[-0.03em]">Willkommen zurück.</h2>
            <p className="mt-3 text-sm leading-6 text-white/45">Melden Sie sich an, um Ihr CashFUX Dashboard zu öffnen.</p>

            <form onSubmit={handleSubmit} className="mt-10 space-y-6">
              <label className="block">
                <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-white/45">E-Mail-Adresse</span>
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  autoComplete="email"
                  required
                  autoFocus
                  data-testid={LOGIN.emailInput}
                  className="h-14 w-full border border-white/12 bg-white/[0.035] px-4 text-sm text-white outline-none transition focus:border-[#e01e37] focus:bg-white/[0.055]"
                  placeholder="name@unternehmen.de"
                />
              </label>

              <label className="block">
                <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-white/45">Passwort</span>
                <span className="relative block">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    autoComplete="current-password"
                    required
                    data-testid={LOGIN.passwordInput}
                    className="h-14 w-full border border-white/12 bg-white/[0.035] px-4 pr-14 text-sm text-white outline-none transition focus:border-[#e01e37] focus:bg-white/[0.055]"
                    placeholder="••••••••••••"
                  />
                  <button type="button" onClick={() => setShowPassword((value) => !value)} className="absolute inset-y-0 right-0 flex w-14 items-center justify-center text-white/35 hover:text-white" aria-label={showPassword ? "Passwort verbergen" : "Passwort anzeigen"}>
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </span>
              </label>

              {error ? <p className="border-l-2 border-[#e01e37] bg-[#e01e37]/8 px-4 py-3 text-sm text-red-200" role="alert">{error}</p> : null}

              <button
                type="submit"
                disabled={submitting}
                data-testid={LOGIN.submitButton}
                className="group flex h-14 w-full items-center justify-between bg-[#e01e37] px-5 text-sm font-semibold transition hover:bg-[#c91830] disabled:cursor-wait disabled:opacity-60"
              >
                <span>{submitting ? "Anmeldung läuft …" : "Sicher anmelden"}</span>
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
            </form>

            <p className="mt-8 text-center text-xs text-white/30">Probleme beim Zugriff? <a href="mailto:kontakt@cashfux.com" className="text-white/60 hover:text-white">Support kontaktieren</a></p>
          </motion.div>
        </section>
      </div>
    </main>
  );
}
